<script>
  import { onMount } from 'svelte';
  import AgreementCard from './AgreementCard.svelte';
  import SigningIdentityDialog from './SigningIdentityDialog.svelte';
  import GatewaySigningModal from './GatewaySigningModal.svelte';
  import { getCurrentRequirements, isHiddenAgreementResponse, reconcileRequirement, resumeSigning, restartLostSigning, restartFailedSigning, restartPendingSigning, pendingRestartConfirmation, pendingRestartUnconfirmed } from './agreementApi.js';

  let loading = true;
  let hidden = false;
  let error = '';
  let requirements = [];
  let selected = null;
  let returnFocus = null;
  let feedback = '';
  let reconciling = new Set();
  let gatewaySession = null;
  let checkingRequirementId = '';
  const checkingFeedback = 'Tikrinama ir saugoma\u2026';
  const finalizationFailedFeedback = 'Para\u0161as egzistuoja, ta\u010diau jo patikra arba saugojimas nepavyko. Automatiniai bandymai sustabdyti. Pakartotin\u0119 patikr\u0105 prad\u0117kite tik ai\u0161kiu veiksmu.';

  async function load() {
    loading = true; error = '';
    try {
      requirements = (await getCurrentRequirements()).filter(item => item.status !== 'withdrawn');
      // Clear stale progress copy as soon as the authoritative API reports a
      // deterministic finalization failure. Automatic verification is stopped.
      if (feedback === checkingFeedback && checkingRequirementId && requirements.some(item =>
        (item.public_id || item.id) === checkingRequirementId && item.status === 'finalization_failed'
      )) {
        feedback = finalizationFailedFeedback;
      }
      return requirements;
    }
    catch (caught) {
      if (isHiddenAgreementResponse(caught)) hidden = true;
      else error = 'Sutarčių būsenos nepavyko įkelti.';
    } finally { loading = false; }
  }

  function startSigning(event) {
    selected = event.detail.requirement;
    returnFocus = event.detail.trigger;
  }

  async function resume(event) {
    const requirement = event.detail.requirement;
    try {
      const result = await resumeSigning(requirement.public_id || requirement.id);
      if (result?.mode !== 'gateway_iframe' || typeof result?.launch_url !== 'string') throw new Error('invalid_resume');
      gatewaySession = { requirement, launchUrl: result.launch_url, returnFocus: event.detail.trigger };
    } catch (_) { feedback = 'Pasirašymo tęsti nepavyko. Atnaujinkite būseną ir bandykite dar kartą.'; }
  }

  async function reconcile(event) {
    const item = event.detail.requirement;
    const id = item.public_id || item.id;
    if (reconciling.has(id)) return;
    reconciling = new Set(reconciling).add(id);
    checkingRequirementId = id;
    feedback = checkingFeedback;
    try {
      await reconcileRequirement(id, event.detail.retryFinalization === true);
      const current = (await load())?.find(requirement => (requirement.public_id || requirement.id) === id);
      if (current?.status === 'finalization_failed') feedback = finalizationFailedFeedback;
      else if (current?.status === 'completed_stored') feedback = 'Pasirašytas dokumentas patikrintas ir išsaugotas.';
      else if (!['signed_storage_pending', 'create_unknown'].includes(current?.status)) feedback = '';
    }
    catch (_) { feedback = 'Būsenos atnaujinti nepavyko. Bandykite vėliau.'; }
    finally {
      const next = new Set(reconciling); next.delete(id); reconciling = next;
      if (checkingRequirementId === id) checkingRequirementId = '';
    }
  }

  async function submitted(event) {
    if (event.detail.mode === 'gateway_iframe') {
      gatewaySession = { requirement: selected, launchUrl: event.detail.result.launch_url, returnFocus };
      selected = null;
      await load();
      return;
    }
    feedback = event.detail.mode === 'invitation_sent' ? 'Kvietimas pasirašyti išsiųstas el. paštu.' : checkingFeedback;
    selected = null;
    await load();
  }

  async function restartLocal(event) {
    const item = event.detail.requirement;
    feedback = 'Atkuriama vietinė bandomoji būsena…';
    try { await restartLostSigning(item.public_id || item.id); await load(); feedback = 'Galite pradėti naują bandomąjį pasirašymą.'; }
    catch (_) { feedback = 'Vietinės bandomosios būsenos atkurti nepavyko.'; }
  }

  async function restartSigning(event) {
    const item = event.detail.requirement;
    const id = item.public_id || item.id;
    const pending = item.status === 'signing_in_progress' && item.can_restart_pending === true;
    if ((!pending && !item.can_restart_signing) || reconciling.has(id)) return;
    if (!window.confirm(pending ? pendingRestartConfirmation : 'Ankstesnis parašas jau egzistuoja. Naujas bandymas jo neatšauks ir gali sunaudoti papildomą pasirašymą. Ankstesnio bandymo istorija bus išsaugota. Pradėti naują pasirašymą?')) return;
    reconciling = new Set(reconciling).add(id);
    try {
      const current = pending
        ? await restartPendingSigning(id, item.pending_restart_transaction_id)
        : await restartFailedSigning(id, item.restart_transaction_id);
      await load();
      if (current?.status !== 'assigned' || current?.can_initiate !== true) throw new Error('restart_not_ready');
      feedback = 'Ankstesnio bandymo istorija išsaugota. Pradedamas naujas pasirašymas.';
      returnFocus = event.detail.trigger;
      selected = current;
    } catch (error) {
      feedback = error?.code === 'signing_restart_unconfirmed' ? pendingRestartUnconfirmed : 'Naujo pasirašymo pradėti nepavyko. Atnaujinkite būseną ir bandykite dar kartą.';
      await load();
    } finally {
      const next = new Set(reconciling); next.delete(id); reconciling = next;
    }
  }

  onMount(async () => {
    await load();
    const params = new URLSearchParams(window.location.search);
    const returnedId = params.get('agreement');
    const isReturn = params.get('dokobit_return') === '1';
    if (isReturn) {
      // Provider redirects may append transient tokens and status fields. Use
      // only our return marker and requirement id, then remove every query
      // parameter before reconciliation, focus changes, or further navigation.
      window.history.replaceState(window.history.state, '', `${window.location.pathname}#/account`);
    }
    if (isReturn && returnedId && requirements.some(item => (item.public_id || item.id) === returnedId)) {
      feedback = checkingFeedback;
      await reconcile({ detail: { requirement: requirements.find(item => (item.public_id || item.id) === returnedId) } });
      document.getElementById(`agreement-${returnedId}`)?.focus();
    }
  });
</script>

{#if !hidden && (error || requirements.length)}
  <section class="agreements" aria-labelledby="agreements-heading" aria-busy={loading}>
    <h2 id="agreements-heading">Sutartys</h2>
    {#if error}<div class="load-error" role="alert"><span>{error}</span><button type="button" on:click={load}>Bandyti dar kartą</button></div>{/if}
    {#if feedback}<p class="feedback" aria-live="polite">{feedback}</p>{/if}
    <div class="cards">
      {#each requirements as requirement (requirement.public_id || requirement.id)}
        <AgreementCard {requirement} busy={reconciling.has(requirement.public_id || requirement.id)} on:sign={startSigning} on:resume={resume} on:reconcile={reconcile} on:restartlocal={restartLocal} on:restartsigning={restartSigning}/>
      {/each}
    </div>
  </section>
{/if}

<SigningIdentityDialog requirement={selected} open={!!selected} {returnFocus} on:close={() => selected = null} on:submitted={submitted}/>
<GatewaySigningModal requirement={gatewaySession?.requirement} launchUrl={gatewaySession?.launchUrl || ''} open={!!gatewaySession} returnFocus={gatewaySession?.returnFocus} on:close={() => gatewaySession = null} on:refresh={load} on:complete={async () => { gatewaySession = null; await load(); }}/>

<style>
  .agreements{width:100%;border-top:1px solid #e0e0e0;padding-top:20px}.agreements h2{font-size:20px;color:#14695b;margin:0 0 14px}.cards{display:flex;flex-direction:column;gap:14px}.load-error{border:1px solid #fecaca;background:#fee2e2;color:#b91c1c;padding:12px;border-radius:8px;display:flex;justify-content:space-between;align-items:center;gap:10px}.load-error button{min-height:44px;border:1px solid #14695b;border-radius:8px;background:white;color:#14695b;padding:8px 12px}.feedback{color:#1e40af;background:#dbeafe;border-radius:8px;padding:10px}
</style>
